/**
 * 태그
 */
import { useEffect, useState, useMemo, useCallback, Fragment } from 'react';
import { useLoaderData, useSearchParams } from "react-router-dom";
import { apiCommunitySlice } from "@features/api/apiCommunitySlice";
import { setRequestToSearchParams } from '@utils/index';
import type { ILoaderData, ITag } from "@type/index";
import { ALIAS, CUR_NETWORK, FILTER_DATA, NETWORK, SEARCH_PARAMS, SERVICE_ALIAS, TAG_URL } from "@constants/index";
import { getLocalData } from "@features/api/apiLocalSlice";
import { useAppSelector } from "@hooks/defineRtkTypedHook";
import { selectL10N, setAlias } from "@features/ui/UiSlice";
import { TagArticle } from "@components/contents/article/TagArticle";
import { useOption } from '@hooks/useOption';
import { NoData } from '../../components/contents/common/NoData';
import { FilterWrap } from '@components/contents/common/FilterWrap';
import { Header } from './Header';
import usePrevious from '@hooks/usePrevious';

let prevParams: {[key: string]: string} = {};
let result: ITag | null = null;


export const TagLoader = (dispatch, option: IOption) =>
async ({ request, params }): Promise<ITag | null> => {
    const searchParams = setRequestToSearchParams(request);

    // alias 변경 - searchParams 변경에 사용됨
    dispatch(setAlias(ALIAS.TAG));

    if(!searchParams?.keyword) {
        return null;
    }

    if(new URLSearchParams(searchParams).toString() === new URLSearchParams(prevParams).toString()) {
        return result;
    }

    // 키워드 변경 시 lastItemCnt 제거
    if(searchParams[SEARCH_PARAMS.KEYWORD] !== prevParams[SEARCH_PARAMS.KEYWORD]) {
        delete searchParams[SEARCH_PARAMS.LAST_ITEM_CNT];
    }

    prevParams = searchParams;
    result = CUR_NETWORK === NETWORK.OPDEV ? await getLocalData(`${ALIAS.TAG}`, searchParams) : await dispatch(apiCommunitySlice?.endpoints?.getCommunityTag?.initiate({ option, params: searchParams }));

    return result;
};

// L2에 태그 필터 있음
interface IFilterComponentProps {
    serviceAlias: string; 
    setTempData: (tempData: ITag['front']) => void;
}
const FilterComponent = ({ serviceAlias, setTempData }: IFilterComponentProps) => {
    const filterChangeCallback = useCallback(() => {
        setTempData([]);
    }, []);

    return <Header>
                <FilterWrap 
                    filterDataObj={serviceAlias ? FILTER_DATA[serviceAlias][ALIAS.TAG] : {}} 
                    changeCallback={filterChangeCallback} />
            </Header>;
};

interface IContentComponentProps {
    data: ITag; 
    tempData: ITag['front'];
    setLastItemCnt: (lastItemCnt: number) => void;
    option: IOption;
}
const ContentComponent = ({ data, tempData, setLastItemCnt, option }: IContentComponentProps) => {
    const MIN_PAGE = 20;
    const l10n = useAppSelector(selectL10N);
    const isBtnMoreShow = useMemo(() => data?.front?.length >= MIN_PAGE, [data]);

    const clickCallback = useCallback(() => {
        setLastItemCnt(tempData?.length || 0);
    }, [tempData]);

    return <Fragment>
                <div className="search-content">
                    <ul className="column column-2">
                        {
                            Array.isArray(tempData) && tempData?.map((item, i) => 
                            <li key={`${data?.keyword}-${i}`} className="search-item">
                                <TagArticle item={item} l10n={l10n!} keyword={data?.keyword} option={option}/>
                            </li>)
                        }
                    </ul>
                </div>
                {
                    isBtnMoreShow ? 
                    <div className="search-paging-more">
                        <button type="button" className="search-paging-more-btn" onClick={clickCallback}>{l10n?.template?.more}</button>
                    </div> : <Fragment />
                }
            </Fragment>
};

export const Tag = () => {
    const { data, isLoading, isError } = useLoaderData() as ILoaderData<ITag> || {};
    const option = useOption();
    const { serviceAlias } = option;
    const [searchParams, setSearchParams] = useSearchParams();
    const [tempData, setTempData] = useState<ITag['front']>([]);
    const [lastItemCnt, setLastItemCnt] = useState<number>(0);
    const previous = usePrevious(searchParams?.get(SEARCH_PARAMS.KEYWORD));

    useEffect(() => {
        if(!Array.isArray(data?.front) || data?.front?.length === 0) {
            setTempData([]);
            setLastItemCnt(0);
            return;
        }

        // 키워드 변경
        if(previous && previous !== searchParams?.get(SEARCH_PARAMS.KEYWORD)) {
            setTempData([...data?.front]);
            setLastItemCnt(0);
        } else {
            setTempData([...tempData, ...data?.front]);
            //setLastItemCnt(lastItemCnt + data?.front?.length);
        }
        
    }, [data]);

    useEffect(() => {
        const lastItemCntParams = searchParams?.get(SEARCH_PARAMS.LAST_ITEM_CNT);
        
        if(!lastItemCnt) {
            searchParams?.delete(SEARCH_PARAMS.LAST_ITEM_CNT);
            setSearchParams(searchParams);
        } else {
            searchParams.set(SEARCH_PARAMS.LAST_ITEM_CNT, `${lastItemCnt}`);
            setSearchParams(searchParams);
        }
    }, [lastItemCnt]);

    if(isError || !Array.isArray(data?.front) || data?.front?.length === 0) {
        return <NoData />;
    }
    
    return <div className="search-result tagList">
        {
            serviceAlias === SERVICE_ALIAS.LINEAGE2 ? 
            <FilterComponent serviceAlias={serviceAlias} setTempData={setTempData} /> : <Fragment />
        }
        <ContentComponent 
            data={data} 
            tempData={tempData} 
            setLastItemCnt={setLastItemCnt}
            option={option} />
    </div>;
};